import { useState, useEffect, useCallback, useMemo } from 'react';
import { thingsboardService } from '../services/thingsboard';
import { wsManager } from '../services/websocket';

export interface TelemetryData {
  [key: string]: {
    ts: number;
    value: string;
  }[];
}

export function useTelemetry(
  entityId: string | null,
  keys: string[],
  entityType: 'DEVICE' | 'ASSET' = 'DEVICE',
  historyMinutes: number = entityType === 'DEVICE' ? 30 : 0
) {
  const [data, setData] = useState<TelemetryData>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const keysString = keys.join(',');
  const keysList = useMemo(() => keysString.split(',').filter(Boolean), [keysString]);

  // Fetch initial REST data: latest + last 30 minutes timeseries
  const fetchInitialData = useCallback(async () => {
    if (!entityId || !entityId.trim()) return false;
    setLoading(true);
    try {
      const now = Date.now();
      const startTs = historyMinutes > 0 ? now - historyMinutes * 60 * 1000 : now;

      const [latestResult, historyResult] = await Promise.all([
        thingsboardService.getLatestTelemetry(entityType, entityId, keysString).catch((err) => {
          console.warn('Failed to fetch latest telemetry', err);
          return {} as TelemetryData;
        }),
        historyMinutes > 0
          ? thingsboardService
              .getTimeseries(
                entityType,
                entityId,
                keysString,
                startTs,
                now,
                0,
                1000,
                'NONE'
              )
              .catch((err) => {
                console.warn('Failed to fetch historical telemetry for realtime', err);
                return {} as TelemetryData;
              })
          : Promise.resolve({} as TelemetryData)
      ]);

      setData((prevData) => {
        const mergedData: TelemetryData = { ...prevData };
        let hasChanges = false;

        keysList.forEach((k) => {
          const histPoints = (historyResult && historyResult[k]) || [];
          const latestPoints = (latestResult && latestResult[k]) || [];
          const currentPrev = prevData[k] || [];

          // Combine: live WS points received in the meantime + latest REST + 30m history
          const allPoints = [...currentPrev, ...latestPoints, ...histPoints];

          // Deduplicate by timestamp
          const seenTs = new Set<number>();
          const unique: { ts: number; value: string }[] = [];
          for (const p of allPoints) {
            if (p && typeof p.ts === 'number' && !seenTs.has(p.ts)) {
              seenTs.add(p.ts);
              unique.push(p);
            }
          }

          // Sort descending (newest first, index 0 is always latest)
          unique.sort((a, b) => b.ts - a.ts);

          // Retain sliding window of (historyMinutes + 5) minutes, max 1000 points
          const cutoff = Date.now() - (historyMinutes > 0 ? historyMinutes + 5 : 35) * 60 * 1000;
          const filtered = unique.filter((p) => p.ts >= cutoff).slice(0, 1000);
          mergedData[k] = filtered;

          if (filtered.length > 0 || currentPrev.length > 0) {
            hasChanges = true;
          }
        });

        return hasChanges ? mergedData : prevData;
      });

      setError(null);
      return true;
    } catch (err: any) {
      console.error('Failed to fetch initial telemetry', err);
      setError(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [entityId, keysString, entityType, historyMinutes, keysList]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let isMounted = true;

    const doFetch = async () => {
      await fetchInitialData();
      if (isMounted) {
        timeoutId = setTimeout(() => {
          doFetch();
        }, 30000); // 30 seconds periodic refresh
      }
    };

    doFetch();

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [fetchInitialData]);

  // Subscribe to real-time updates via WebSocket
  useEffect(() => {
    if (!entityId || !entityId.trim()) return;

    // Connect WS if not connected
    wsManager.connect();

    const subId = wsManager.subscribeTelemetry(entityType, entityId, keysString, (incomingData: any) => {
      // incomingData format: { "temperature": [[1612... , "24.5"]] }
      setData((prevData) => {
        const newData = { ...prevData };
        let hasChanges = false;

        Object.keys(incomingData).forEach((key) => {
          if (!Array.isArray(incomingData[key])) return;

          const values = incomingData[key].map((item: any[]) => ({
            ts: item[0],
            value: item[1]
          }));

          const existing = newData[key] || [];
          const existingTs = new Set(existing.map((p) => p.ts));
          const newUnique = values.filter((p) => !existingTs.has(p.ts));

          if (newUnique.length > 0) {
            hasChanges = true;
            // Prepend new live points (newest first)
            const combined = [...newUnique, ...existing];
            combined.sort((a, b) => b.ts - a.ts);

            // Retain data within the last (historyMinutes + 5) minutes, max 1000 points
            const cutoff = Date.now() - (historyMinutes > 0 ? historyMinutes + 5 : 35) * 60 * 1000;
            newData[key] = combined.filter((p) => p.ts >= cutoff).slice(0, 1000);
          }
        });

        return hasChanges ? newData : prevData;
      });
    });

    return () => {
      wsManager.unsubscribe(subId);
    };
  }, [entityId, keysString, entityType, historyMinutes]);

  return { data, loading, error, refetch: fetchInitialData };
}
